'use client';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { Input } from '@/app/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';

const FIRST_PAGE = 1;

interface CustomPaginationProps {
  currentPage: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export const CustomPagination: FC<CustomPaginationProps> = ({ currentPage, total, pageSize, onPageChange }) => {
  const [inputPage, setInputPage] = useState<string>('');
  const totalPages = Math.ceil(total / pageSize);
  const t = useTranslations();

  useEffect(() => {
    setInputPage(String(currentPage));
  }, [currentPage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setInputPage(value);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const page = Number(inputPage);
      if (page > 0 && page <= totalPages) {
        onPageChange(page);
      } else {
        setInputPage(String(currentPage));
      }
    }
  };

  if (totalPages <= 1) return null;
  return (
    <Pagination>
      <PaginationContent className="font-medium text-sm text-system-text03 lg:text-xs">
        <PaginationItem className="cursor-pointer">
          <PaginationPrevious
            className={clsx(currentPage === FIRST_PAGE && 'invisible h-8 w-8')}
            onClick={() => onPageChange(Math.max(FIRST_PAGE, currentPage - 1))}
          />
        </PaginationItem>
        <div className="inline-flex items-center gap-2 px-4">
          <span>{t('payments.credits.page')}</span>
          <div className="relative block w-fit">
            <span className="invisible whitespace-pre px-4">{inputPage}</span>
            <Input
              value={inputPage}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              className="absolute top-[-7px] right-0 left-0 h-8 w-auto rounded-8 border-system-text05 bg-system-white08 px-2 text-center text-base text-system-text03 lg:text-sm"
            />
          </div>
          <span>/</span>
          <span>{totalPages}</span>
        </div>
        <PaginationItem className="cursor-pointer">
          <PaginationNext
            className={clsx(currentPage === totalPages && 'invisible')}
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};
